import { LIVE_CHAT_LIMITS } from "../constants";
import { mergeMessages } from "../utils/messages";

export const initialLiveChatState = {
  status: "idle",
  messages: [],
  token: null,
  wsUrl: null,
  verification: null,
  routing: null,
  awaitingAiReply: false,
  messageRequestInFlight: false,
  customer: null,
  nextBeforeId: null,
  error: null,
  loadingOlder: false,
  olderError: null,
  olderRetryAt: null,
  identityStep: null,
  identityError: null,
  phone: "",
  otp: "",
  resendAvailableAt: null,
  identityRequest: null,
  postVerificationLoading: false,
};

export function isAiAssigned(routing) {
  return routing?.assignee_type === "ai";
}

export function hasUnansweredCustomerMessage(messages = []) {
  let latestCustomerIndex = -1;
  let latestReplyIndex = -1;

  messages.forEach((message, index) => {
    if (message?.actor === "customer" && !message.pending) latestCustomerIndex = index;
    else if (
      message &&
      !message.pending &&
      (message.actor === "ai" || message.message_type === "session_outbound_ai")
    ) {
      latestReplyIndex = index;
    }
  });

  return latestCustomerIndex > latestReplyIndex;
}

function getRoutingState(routing, messages) {
  return {
    routing: routing || null,
    awaitingAiReply: isAiAssigned(routing) && hasUnansweredCustomerMessage(messages),
  };
}

export function requiresInitialVerification(verification) {
  return verification?.verification_required === true;
}

export function isSupportedVerification(verification) {
  return !verification?.verification_method || verification.verification_method === "phone_otp";
}

export function getVerificationState(verification, currentVerification = null) {
  if (verification && typeof verification === "object") return verification;
  return {
    ...(currentVerification || {}),
    message_send_allowed: null,
  };
}

export function liveChatReducer(state, action) {
  switch (action.type) {
    case "BOOTSTRAP_START":
      return { ...state, status: "loading", error: null };
    case "BOOTSTRAP_SUCCESS": {
      const messages = mergeMessages(state.messages, action.data.messages);
      return {
        ...state,
        status: action.unsupported ? "unsupported" : "ready",
        token: action.data.token,
        wsUrl: action.data.ws_url,
        verification: action.data.verification,
        ...getRoutingState(action.data.routing, messages),
        customer: action.data.customer,
        nextBeforeId: action.data.next_before_id,
        messages,
        identityStep: action.identityStep,
        error: null,
      };
    }
    case "BOOTSTRAP_ERROR":
      return { ...state, status: "error", error: action.error };
    case "MERGE_MESSAGES":
      return { ...state, messages: mergeMessages(state.messages, action.messages) };
    case "ROUTING_MESSAGES": {
      const messages = mergeMessages(state.messages, action.messages);
      return {
        ...state,
        messages,
        ...getRoutingState(action.routing || state.routing, messages),
      };
    }
    case "ADD_PENDING":
      return { ...state, messages: mergeMessages(state.messages, [action.message]), error: null };
    case "MESSAGE_REQUEST_START":
      return { ...state, messageRequestInFlight: true };
    case "MESSAGE_REQUEST_END":
      return { ...state, messageRequestInFlight: false };
    case "MESSAGE_RETRYING":
      return {
        ...state,
        messages: state.messages.map((message) =>
          message.client_message_id === action.clientMessageId
            ? {
                ...message,
                pending: true,
                failed: false,
                waitingForVerification: false,
                error: null,
                retryAt: null,
              }
            : message
        ),
      };
    case "MESSAGE_FAILED":
      return {
        ...state,
        messages: state.messages.map((message) =>
          message.client_message_id === action.clientMessageId
            ? {
                ...message,
                pending: false,
                failed: true,
                waitingForVerification: false,
                error: action.error,
                retryAt: action.retryAt || null,
              }
            : message
        ),
      };
    case "MESSAGE_WAITING_FOR_VERIFICATION":
      return {
        ...state,
        messages: state.messages.map((message) =>
          message.client_message_id === action.clientMessageId
            ? {
                ...message,
                pending: true,
                failed: false,
                waitingForVerification: true,
                error: null,
                retryAt: null,
              }
            : message
        ),
      };
    case "REMOVE_PENDING":
      return {
        ...state,
        messages: state.messages.filter(
          (message) => message.client_message_id !== action.clientMessageId
        ),
      };
    case "VERIFICATION": {
      const verification = getVerificationState(action.verification, state.verification);
      const supported = isSupportedVerification(verification);
      return {
        ...state,
        status: supported ? state.status : "unsupported",
        verification,
        identityStep: supported ? "phone" : null,
        identityError: null,
      };
    }
    case "UPDATE_VERIFICATION": {
      const verification = getVerificationState(action.verification, state.verification);
      const supported = isSupportedVerification(verification);
      return {
        ...state,
        status: supported ? state.status : "unsupported",
        verification,
        identityStep:
          supported && verification?.verification_required === true
            ? state.identityStep || "phone"
            : state.identityStep,
      };
    }
    case "IDENTITY_INPUT":
      return { ...state, [action.field]: action.value, identityError: null };
    case "OTP_REQUEST_START":
      return { ...state, identityRequest: action.request, identityError: null };
    case "OTP_SENT":
      return {
        ...state,
        identityStep: "otp",
        identityRequest: null,
        identityError: null,
        resendAvailableAt: Date.now() + LIVE_CHAT_LIMITS.resendCooldownSeconds * 1000,
      };
    case "OTP_ERROR":
      return { ...state, identityRequest: null, identityError: action.error };
    case "EDIT_PHONE":
      return { ...state, identityStep: "phone", otp: "", identityError: null };
    case "OTP_VERIFIED":
      return {
        ...state,
        token: action.conversation.token,
        wsUrl: action.wsUrl,
        verification: action.conversation.verification,
        customer: action.conversation.customer,
        identityStep: null,
        identityRequest: null,
        identityError: null,
        otp: "",
        postVerificationLoading: true,
        messages: mergeMessages(
          state.messages,
          action.offlineMessage ? [action.offlineMessage] : []
        ),
      };
    case "VERIFIED_CHAT_READY":
      return { ...state, postVerificationLoading: false };
    case "OLDER_START":
      return { ...state, loadingOlder: true, olderError: null };
    case "OLDER_DONE": {
      const verification = getVerificationState(action.data.verification, state.verification);
      const messages = mergeMessages(state.messages, action.data.messages);
      const routing = action.data.routing || state.routing;
      return {
        ...state,
        loadingOlder: false,
        olderError: null,
        olderRetryAt: null,
        messages,
        ...getRoutingState(routing, messages),
        nextBeforeId: action.data.next_before_id,
        verification,
        identityStep:
          verification?.verification_required === true
            ? state.identityStep || "phone"
            : state.identityStep,
      };
    }
    case "OLDER_ERROR":
      return {
        ...state,
        loadingOlder: false,
        olderError: action.error,
        olderRetryAt: action.retryAt,
      };
    case "SET_ERROR":
      return { ...state, error: action.error };
    default:
      return state;
  }
}
